import {Component} from 'panel';
import {registerMPElement} from '../../util/register-element';
import {SECONDS_BY_UNIT} from '../../util/date';
import {ENTER_KEY_CODE} from 'common/util/dom';

import template from './index.jade';
import css from './index.styl?inline';

export interface Bucket {
  bucketSize: number;
  max: number;
  min: number;
  offset: number;
}

interface ChartState {
  windowErrorMsg: null | string;
  isMenuOpen: boolean;
  editedUnit: string;
  editedBucket: Bucket;
}

/** widget for selecting bucket size, min, and max. */
class BucketSelector extends Component<ChartState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      label: {type: `string`, description: `label before selector`},
      units: {
        type: `json`,
        description: `interface: string[]`,
      },
      'selected-bucket': {type: `json`, description: `Currently selected bucket`},
      'selected-unit': {type: `string`, description: `Currently selected unit`},
      'reset-bucket': {
        type: `boolean`,
        default: false,
        description: `whether to display reset button`,
      },
      'max-num-buckets': {
        type: `number`,
        default: 90,
        description: `provide a max number of buckets allowed for error handling`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        windowErrorMsg: null,
        isMenuOpen: false,
        editedUnit: null,
        editedBucket: null,
      },
      helpers: {
        applyBucketSelections: () => {
          this.update({isMenuOpen: false});
          this.dispatchEvent(
            new CustomEvent(`changeSelectedBucket`, {
              detail: {
                bucketSize: this.helpers.selectedUnitToVal(this.state.editedBucket.bucketSize, this.state.editedUnit),
                max: this.helpers.selectedUnitToVal(this.state.editedBucket.max, this.state.editedUnit),
                min: this.helpers.selectedUnitToVal(this.state.editedBucket.min, this.state.editedUnit),
                offset: this.state.editedBucket.offset,
              },
            }),
          );
        },
        changeMenu: (ev) => {
          if (!ev.detail.isOpen) {
            this.update({
              windowErrorMsg: null,
              isMenuOpen: false,
            });
          } else {
            const bucket = this.attr(`selected-bucket`);
            const unit = this.attr(`selected-unit`);
            this.update({
              isMenuOpen: true,
              editedUnit: unit,
              editedBucket: {
                bucketSize: this.helpers.valToSelectedUnit(bucket.bucketSize, unit),
                max: this.helpers.valToSelectedUnit(bucket.max, unit),
                min: this.helpers.valToSelectedUnit(bucket.min, unit),
                offset: bucket.offset,
              },
            });
          }
        },
        changeBucketSize: (ev) => {
          const editedBucket = {...this.state.editedBucket, bucketSize: parseInt(ev.target.value, 10)};
          this.update({windowErrorMsg: this.validate(editedBucket), editedBucket});
        },
        changeBucketUnit: (ev) => {
          const {value: newUnit, isSelected} = ev.detail.item;
          if (!isSelected) {
            this.update({
              editedUnit: newUnit,
            });
          }
        },
        changeBucketMax: (ev) => {
          const editedBucket = {...this.state.editedBucket, max: parseInt(ev.target.value, 10)};
          this.update({windowErrorMsg: this.validate(editedBucket), editedBucket});
        },
        changeBucketMin: (ev) => {
          const editedBucket = {...this.state.editedBucket, min: parseInt(ev.target.value, 10)};
          this.update({windowErrorMsg: this.validate(editedBucket), editedBucket});
        },
        resetInterval: () => {
          this.update({isMenuOpen: false});
          this.dispatchEvent(new CustomEvent(`resetBucket`, {detail: null}));
        },
        inputInserted: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
            vnode.elm.select();
          }),
        showDropDown: () => {
          return this.attr(`units`) && this.attr(`units`).length > 1;
        },
        getBucketUnits: () => {
          return [
            {
              items: this.attr(`units`).map((unit) => ({
                value: unit,
                label: unit,
                isSelected: unit === this.state.editedUnit,
              })),
            },
          ];
        },
        getDisplayedBucketLabel: () => {
          const transformedBucketSize = this.helpers.valToSelectedUnit(
            this.attr(`selected-bucket`).bucketSize,
            this.attr(`selected-unit`),
          );
          return `interval size: ${transformedBucketSize} ${this.attr(`selected-unit`)}`;
        },
        getBucketSize: () => {
          return this.state.editedBucket.bucketSize;
        },
        getMax: () => {
          return this.state.editedBucket.max;
        },
        getMin: () => {
          return this.state.editedBucket.min;
        },
        /** Transforms if unit is in default transform eg Minutes, Hours, Days */
        valToSelectedUnit: (val: number, unit: string) => {
          if (!SECONDS_BY_UNIT.hasOwnProperty(unit)) {
            return val;
          }
          return val / SECONDS_BY_UNIT[unit];
        },
        selectedUnitToVal: (unitVal: number, unit: string) => {
          if (!SECONDS_BY_UNIT.hasOwnProperty(unit)) {
            return unitVal;
          }
          return unitVal * SECONDS_BY_UNIT[unit];
        },
        handleKeyUp: (ev) => {
          if (ev.keyCode === ENTER_KEY_CODE) {
            this.helpers.applyBucketSelections();
          }
        },
      },
    };
  }
  validate(bucket: Bucket): string | null {
    let error = null;
    const difference = bucket.max - bucket.min;
    if (difference < 0) {
      error = `Invalid interval: Max must be greater than Min`;
    } else if (difference / bucket.bucketSize > this.attr(`max-num-buckets`)) {
      error = `Invalid interval: Number of intervals cannot exceed ${this.attr(`max-num-buckets`)}`;
    } else if (bucket.bucketSize > difference) {
      error = `Invalid interval: interval cannot be greater than the given range`;
    }

    return error;
  }
}

export default registerMPElement(`mp-bucket-selector`, BucketSelector);
